import { useGetAccountsQuery } from "@/modules/account/services";
import { useGetOwnTransferTemplateQuery } from "@/modules/template/services";
import { TransactionTypes, TransferKeys } from "@/modules/transaction/common/enum";
import type {
  IOwnAccountsTransferForm,
  IOwnAccountTransferRequest
} from "@/modules/transaction/common/types";
import { SaveAsTemplate } from "@/modules/transaction/components/SaveAsTemplate";
import { useOwnAccountTransfer } from "@/modules/transaction/hooks";
import {
  useCreateOwnTransferMutation,
  useGetOwnTransferQuery,
  useUpdateOwnTransferMutation,
} from "@/modules/transaction/services";
import {
  CURRENCY,
  DATE_FORMAT,
  INITIAL_PAGE_SIZE,
  INPUT_MAX_LIMIT,
} from "@/shared/common/constants";
import { SearchParamKeys } from "@/shared/common/enums";
import type { IExtendedObject } from "@/shared/common/types";
import { OwnAccountSelectOption, Select, TransferLayout } from "@/shared/components";
import {
  clampToCurrentDate,
  cleanupAmount,
  formatDate,
  isValidAmount,
  mergeByKeys
} from "@/shared/helpers";
import { useHasFormChanges } from "@/shared/hooks";
import { CollapseGroup } from "@ab.uitools/ui-library/components/Collapse";
import { SimpleDatePicker } from "@ab.uitools/ui-library/components/DatePicker";
import { FormField } from "@ab.uitools/ui-library/components/FormField";
import { Input } from "@ab.uitools/ui-library/components/Input";
import { Textarea } from "@ab.uitools/ui-library/components/Textarea";
import type { TItemValue } from "@ab.uitools/ui-library/types/globalTypes";
import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";
import uuid4 from "uuid4";

interface Props {
  isEdit?: boolean;
  isCopy?: boolean;
  isNameUnique: boolean;
  setIsNameUnique: Dispatch<SetStateAction<boolean>>;
}

export const OwnAccountsTransferForm = ({
  isCopy, isEdit, isNameUnique, setIsNameUnique
}: Props) => {
  const { t, i18n: { language } } = useTranslation();
  const { operationId = "" } = useParams();
  const [searchParams] = useSearchParams();
  const toAccount = searchParams.get(SearchParamKeys.ToAccount);
  const templateId = searchParams.get(SearchParamKeys.TemplateId);
  const idempotencyKey = useRef(uuid4()).current;

  const [defaultData, setDefaultData] = useState<IExtendedObject>({});

  const {
    data: template,
    isLoading: isGetTemplateLoading,
  } = useGetOwnTransferTemplateQuery({
    id: templateId as string,
  }, {
    refetchOnMountOrArgChange: true,
    skip: (!isEdit || !isCopy) && !templateId
  });

  const [createOwnTransferApi, {
    isLoading: isCreateTransferLoading
  }] = useCreateOwnTransferMutation();
  const [updateOwnTransferApi, { isLoading: isUpdateTransferLoading }] = useUpdateOwnTransferMutation();

  const {
    data: accountsList = [],
    isLoading: isGetAccountsLoading,
  } = useGetAccountsQuery({
    pageSize: INITIAL_PAGE_SIZE,
  }, {
    refetchOnMountOrArgChange: true,
  });

  const {
    reset,
    errors,
    values,
    setValue,
    creditAccountsFilteredList,
    debitAccountsFilteredList,
    handleClickDebitTarget,
    handleClickCreditTarget,
    handleSetCreditDebitAccountLists,
  } = useOwnAccountTransfer({ accountsList, isEdit });

  const {
    data: ownTransfer,
    isLoading: isGetTransferLoading,
  } = useGetOwnTransferQuery({
    id: operationId
  }, {
    refetchOnMountOrArgChange: true,
    skip: (!isEdit || !isCopy) && !operationId
  });

  const ownAccountTransferAction = useCallback((values: IOwnAccountsTransferForm) => {
    const requestData: IOwnAccountTransferRequest = {
      ...values,
      scheduleDate: formatDate(values.scheduleDate, DATE_FORMAT.YYYY_MM_DD),
      amount: +cleanupAmount(`${values.amount}`),
      debitAccount: accountsList?.find(({ number }) => number === values.debitAccount),
      creditAccount: accountsList?.find(({ number }) => {
        return number === values.creditAccount;
      }),
    };

    return isEdit ?
      updateOwnTransferApi({
        data: requestData,
        id: `${ownTransfer?.id}`
      }).unwrap() :
      createOwnTransferApi({
        data: requestData,
        idempotencyKey,
      }).unwrap();
  }, [accountsList, ownTransfer]);

  const setDefaultValues = () => {
    const data = {
      creditAccount: ownTransfer?.creditAccount.number,
      debitAccount: ownTransfer?.debitAccount.number,
      transactionNumber: isEdit ?
        ownTransfer?.transactionNumber : values.transactionNumber,
      scheduleDate: clampToCurrentDate(ownTransfer?.scheduleDate),
      amount: ownTransfer?.amount,
      tin: ownTransfer?.tin,
      purpose: ownTransfer?.purpose,
      currency: ownTransfer?.currency,
    };
    reset?.(data);
    setDefaultData({
      ...data,
      scheduleDate: new Date(ownTransfer?.scheduleDate as string),
    });
    handleSetCreditDebitAccountLists(
      `${ownTransfer?.creditAccount.number}`,
      ownTransfer?.debitAccount
    );
  };

  const currentData = useMemo(() => ({
    creditAccount: values.creditAccount,
    debitAccount: values.debitAccount,
    transactionNumber: values.transactionNumber,
    scheduleDate: new Date(values.scheduleDate),
    amount: +cleanupAmount(values.amount),
    tin: values.tin,
    purpose: values.purpose,
    currency: values.currency,
  }), [values]);

  const hasChanges = useHasFormChanges(defaultData, currentData);

  useEffect(() => {
    if ((isEdit || isCopy) && !!ownTransfer?.id) {
      setDefaultValues();
    }
  }, [isEdit, isCopy, ownTransfer]);

  useEffect(() => {
    if (!isEdit && toAccount) {
      setValue(TransferKeys.CreditAccount, toAccount);
    }
  }, [toAccount]);

  useEffect(() => {
    if (template) {
      const data = mergeByKeys(values, {
        ...template,
        currency: template?.debitAccount?.currency,
        debitAccount: template?.debitAccount?.number,
        creditAccount: template?.creditAccount?.number,
      });
      reset?.(data);
    }
  }, [template]);

  useEffect(() => {
    if (!isEdit) {
      setValue(TransferKeys.ScheduleDate, new Date());
    }
    if (!isEdit && !isCopy && !templateId) {
      setValue(TransferKeys.Purpose, t("transfer.ownAccounts.purpose"));
    }
  }, []);

  return (
    <TransferLayout
      title={t("transfer.ownAccounts.title")}
      page="Own account transfer"
      isEdit={isEdit}
      withCommission={false}
      hasChanges={hasChanges}
      accountsList={accountsList}
      getTransferId={ownAccountTransferAction}
      isSignDisabled={!isNameUnique && values.isSaveAsTemplate}
      isSaveDisabled={!isNameUnique && values.isSaveAsTemplate}
      transactionType={TransactionTypes.OwnAccountTransfer}
      isLoading={isCreateTransferLoading || isUpdateTransferLoading}
      isGetTransferLoading={isGetTransferLoading || isGetTemplateLoading}
    >
      <CollapseGroup
        className="collapse-group"
        items={[
          {
            content: <>
              <FormField
                name="debitAccount"
                className="mb-32"
                errorMessageIcon="infoFilled"
                shouldValidate
                As={(props) => (
                  <Select
                    {...props}
                    required
                    isSearchable
                    className="custom-select"
                    options={debitAccountsFilteredList}
                    isDisabled={isGetAccountsLoading}
                    label={t("transfer.debitAccount")}
                    placeholder={t("transfer.chooseDebitAccount")}
                    setSelectedValue={(value) => handleClickDebitTarget(value as TItemValue)}
                    components={{ Option: OwnAccountSelectOption }}
                  />
                )}
              />
              <FormField
                name="creditAccount"
                className="mb-8"
                errorMessageIcon="infoFilled"
                shouldValidate
                As={(props) => (
                  <Select
                    {...props}
                    required
                    isSearchable
                    className="custom-select"
                    options={creditAccountsFilteredList}
                    isDisabled={isGetAccountsLoading}
                    label={t("transfer.creditAccount")}
                    placeholder={t("transfer.chooseCreditAccount")}
                    setSelectedValue={(value) => handleClickCreditTarget(value as TItemValue)}
                    components={{ Option: OwnAccountSelectOption }}
                  />
                )}/>
            </>,
            title: t("transfer.accountDetails"),
            value: "account_details",
            isOpen: true,
          },
          {
            content: <>
              <FormField
                name="amount"
                className="mb-32"
                errorMessageIcon="infoFilled"
                As={(props) => (
                  <Input
                    {...props}
                    required
                    type="numeric"
                    allowNegative={false}
                    allowLeadingZeros={false}
                    placeholder={t("transfer.enterAmount")}
                    thousandSeparator=","
                    decimalScale={values.currency === CURRENCY.AMD.label ? 1 : 2}
                    rightIconProps={{
                      Component: CURRENCY[values.currency]?.symbolIcon || "",
                    }}
                    label={t("general.amount")}
                    isAllowed={({ formattedValue }) => isValidAmount(formattedValue)}
                  />
                )}/>
              <FormField
                name="purpose"
                className="mb-8"
                errorMessageIcon="infoFilled"
                hideErrorMessage
                As={(props) => (
                  <Textarea
                    {...props}
                    required
                    error={`${errors?.purpose?.message || ""}`}
                    label={t("general.purpose")}
                    placeHolder={t("general.enterYourPurpose")}
                    maxCount={INPUT_MAX_LIMIT}
                  />
                )}
              />
              <SaveAsTemplate
                setValue={setValue}
                setIsNameUnique={setIsNameUnique}
                isSaveAsTemplate={values.isSaveAsTemplate}
              />
            </>,
            title: t("transfer.transferDetails"),
            value: "transfer_details",
            isOpen: true,
          },
          {
            content: <>
              <FormField
                name="transactionNumber"
                className="mb-32"
                errorMessageIcon="infoFilled"
                As={(props) => (
                  <Input
                    {...props}
                    required
                    label={t("transfer.transactionNumber")}
                    placeholder={t("transfer.enterTransactionNumber")}
                  />
                )}/>
              <FormField
                name="scheduleDate"
                className="mb-8"
                errorMessageIcon="infoFilled"
                As={(props) => (
                  <SimpleDatePicker
                    {...props}
                    required
                    closeOnScroll
                    locale={language}
                    format={DATE_FORMAT.DD_MM_YYYY}
                    label={t("general.date")}
                    value={props.value as Date}
                    minDate={new Date()}
                  />
                )}/>
            </>,
            title: t("general.otherDetails"),
            value: "other_details",
            isOpen: true,
          },
        ]}/>
    </TransferLayout>
  );
};
